import { useMemo } from 'react';

import { useStorefrontTextTranslator } from '@/features/storefront/i18n/textTranslator';
import { useThemeLayout } from '@/features/storefront/layouts/useThemeLayout';
import { pageSections } from '@/features/storefront/sections/pageDocument';
import { pageAnchorType, type PageId } from '@/features/storefront/sections/pages';
import type { AnchorSection, Section } from '@/features/storefront/sections/schema';
import { useStorefrontTheme } from '@/features/storefront/useStorefrontTheme';
import { themePageDefaults } from '@/previewThemes/registry';

/**
 * What one storefront page is made of, for the page that renders it.
 *
 * Two answers, because a page needs both: the whole stack, which it hands to
 * `SectionList`, and its own anchor, whose settings say how its grid, filters
 * and panels differ from the theme's. Neither is a page's to work out for
 * itself - a shop wearing a theme it has never customised has to render
 * exactly what it rendered before pages were arrangeable, and that resolution
 * has one home.
 *
 * The anchor is never null in practice: `pageSections` repairs a document that
 * has lost one. It is typed nullable for the home page, which owns none.
 */
export function usePageSections(page: PageId): {
  sections: Section[];
  anchor: AnchorSection | null;
} {
  const theme = useStorefrontTheme();
  const layout = useThemeLayout();

  // The theme the *document* names, not the per-area override: a page manifest
  // is a statement about a whole theme, and mixing an editorial header into a
  // classic shop should not change which sections the product page offers.
  const family = layout.family;
  const translate = useStorefrontTextTranslator();

  return useMemo(() => {
    const resolved = pageSections(theme, page, themePageDefaults(family));
    const sections = translate ? translate(resolved) : resolved;
    const anchorType = pageAnchorType(page);
    const anchor =
      (sections.find((section) => section.type === anchorType) as AnchorSection | undefined) ??
      null;
    return { sections, anchor };
  }, [theme, page, family, translate]);
}
